import { useEffect, useState } from "react";
import { X, EnvelopeSimple, CheckCircle, CurrencyDollar } from "@phosphor-icons/react";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const METHODS = [
  { value: "cash",   label: "Cash" },
  { value: "check",  label: "Check" },
  { value: "wire",   label: "Wire transfer" },
  { value: "ach",    label: "ACH" },
  { value: "venmo",  label: "Venmo" },
  { value: "zelle",  label: "Zelle" },
  { value: "paypal", label: "PayPal" },
  { value: "stripe", label: "Stripe (manual)" },
  { value: "other",  label: "Other" },
];

/** Record an offline payment against a custom invoice (mirrors RecordPaymentModal for projects). */
export function RecordInvoicePaymentModal({ open, onClose, invoice, onSaved }) {
  const [mode, setMode] = useState("full");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [paidAt, setPaidAt] = useState(todayIso());
  const [note, setNote] = useState("");
  const [sendReceipt, setSendReceipt] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("full"); setAmount(""); setMethod("cash");
    setPaidAt(todayIso()); setNote(""); setSendReceipt(true);
  }, [open]);

  if (!open || !invoice) return null;

  const total = Number(invoice.total_usd || 0);
  const paid = Number(invoice.paid_amount || 0);
  const balance = Math.max(0, total - paid);
  const enteredAmount = mode === "full" ? balance : Number(amount || 0);
  const willBeFullyPaid = total > 0 && (paid + enteredAmount) >= total - 0.01;

  const validate = () => {
    if (mode === "partial") {
      if (!amount || enteredAmount <= 0) return "Amount is required";
      if (enteredAmount > balance + 0.01) return `Amount exceeds outstanding balance (${formatUSD(balance)})`;
    } else if (balance <= 0) {
      return "No outstanding balance";
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v !== true) return toast.error(v);
    setSaving(true);
    try {
      const body = {
        method,
        paid_at: paidAt || undefined,
        note: note.trim() || undefined,
        send_receipt: sendReceipt,
      };
      if (mode === "full") body.mark_paid_in_full = true;
      else body.amount = enteredAmount;
      const { data } = await api.post(`/admin/invoices/${invoice.id}/record-payment`, body);
      const recMsg = data.receipt_sent ? " & receipt sent" : (sendReceipt ? ` (receipt failed)` : "");
      toast.success(`Recorded ${formatUSD(data.payment.amount_usd)} payment${recMsg}`);
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="record-invoice-payment-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b border-[#262626] bg-[#0a0a0a]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Payment</div>
            <div className="font-display text-xl mt-1">Record payment · {invoice.invoice_number}</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-5">
          <div className="grid grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
            <Cell label="Total" value={formatUSD(total)} />
            <Cell label="Paid" value={formatUSD(paid)} />
            <Cell label="Balance" value={formatUSD(balance)} accent={balance > 0} />
          </div>

          <div className="grid grid-cols-2 gap-px bg-[#262626] border border-[#262626]" role="radiogroup">
            <button type="button" onClick={() => setMode("full")} data-testid="inv-record-mode-full"
              className={`px-3 py-2.5 text-xs uppercase tracking-wider transition-colors ${mode === "full" ? "bg-[#FF2D2D] text-white" : "bg-[#0a0a0a] text-[#a3a3a3] hover:bg-[#121212]"}`}>
              Mark paid in full
            </button>
            <button type="button" onClick={() => setMode("partial")} data-testid="inv-record-mode-partial"
              className={`px-3 py-2.5 text-xs uppercase tracking-wider transition-colors ${mode === "partial" ? "bg-[#FF2D2D] text-white" : "bg-[#0a0a0a] text-[#a3a3a3] hover:bg-[#121212]"}`}>
              Partial payment
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {mode === "partial" && (
              <Field label="Amount" required>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a3a3a3] text-sm">$</span>
                  <input type="number" min="0.01" max={balance} step="0.01" value={amount}
                    onChange={(e) => setAmount(e.target.value)} placeholder={balance.toFixed(2)}
                    data-testid="inv-record-amount"
                    className="w-full bg-[#050505] border border-[#262626] pl-7 pr-3 py-2 text-white tabular-nums focus:border-[#FF2D2D] outline-none" />
                </div>
              </Field>
            )}
            <Field label="Method" required>
              <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="inv-record-method"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none">
                {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>
            <Field label="Paid on">
              <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
                data-testid="inv-record-paidat"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none" />
            </Field>
          </div>

          <Field label="Note (optional, included in receipt)">
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
              placeholder="e.g. Check #1234, received 2026-02-08" data-testid="inv-record-note"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none" />
          </Field>

          <label className="flex items-start gap-3 border border-[#262626] bg-[#050505] p-3 cursor-pointer">
            <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)}
              className="accent-[#FF2D2D] mt-0.5" data-testid="inv-record-send-receipt" />
            <div className="flex-1">
              <div className="flex items-center gap-1.5 text-sm">
                <EnvelopeSimple size={14} weight="fill" className="text-[#FF2D2D]" />
                Email receipt to client
              </div>
              <div className="text-[10px] text-[#a3a3a3] mt-0.5">
                {invoice.client_email || invoice.client_name || "client"}
              </div>
            </div>
          </label>

          <div className="flex justify-end gap-3 pt-3 border-t border-[#262626]">
            <button type="button" onClick={onClose} className="btn-outline px-4 py-2 text-xs uppercase tracking-wider">Cancel</button>
            <button type="submit" disabled={saving || enteredAmount <= 0}
              data-testid="inv-record-submit"
              className="btn-primary px-5 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50">
              {willBeFullyPaid ? <CheckCircle size={12} weight="fill" /> : <CurrencyDollar size={12} weight="bold" />}
              {saving ? "Saving…" : `Record ${formatUSD(enteredAmount)}${sendReceipt ? " + receipt" : ""}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div className="bg-[#0a0a0a] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#a3a3a3]">{label}</div>
      <div className={`font-display text-lg mt-0.5 tabular-nums ${accent ? "text-[#FF2D2D]" : ""}`}>{value}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
        {label} {required && <span className="text-[#FF2D2D]">*</span>}
      </label>
      {children}
    </div>
  );
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
